import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import axios from "axios";
import "react-datepicker/dist/react-datepicker.css";
import { format, addMinutes, setHours, setMinutes, startOfDay } from "date-fns";
import { Clock } from "lucide-react";

import { UserContext } from "../../context/UserContext";
import { getCartByUserId } from "../../services/Cart/CartPage";
import { SlotContext } from "../../context/SlotContext";

export default function Slots() {
  const { user } = useContext(UserContext);
  const { selectedSlot, setSelectedSlot } = useContext(SlotContext);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [storeId, setStoreId] = useState(null);

  // ✅ API Base URL from env
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  /* ================= GET STORE ID ================= */
  useEffect(() => {
    const fetchCart = async () => {
      if (!user?.userId) return;

      try {
        const cart = await getCartByUserId(user.userId);

        if (cart?.items?.length > 0) {
          setStoreId(cart.items[0].storeId);
        }
      } catch (err) {
        console.error("Cart API error:", err);
      }
    };

    fetchCart();
  }, [user]);

  /* ================= FETCH SHIFT ================= */
  useEffect(() => {
    const fetchShift = async () => {
      if (!storeId) return;

      try {
        // ✅ Using env URL
        const resp = await axios.get(
          `${API_BASE_URL}CompanyAPI/GetShiftByStore?storeId=${storeId}`
        );

        generateSlots(resp.data);
      } catch (err) {
        console.error("Shift API error:", err);
      }
    };

    fetchShift();
  }, [storeId, selectedDate, API_BASE_URL]);

  /* ================= GENERATE SLOTS ================= */
  const generateSlots = (shiftData) => {
    const jsDay = selectedDate.getDay();
    const dayOfWeek = jsDay === 0 ? 7 : jsDay;

    const todayShift = shiftData.find(
      (shift) => shift.dayOfWeek === dayOfWeek
    );

    if (!todayShift || !todayShift.openTime || !todayShift.closeTime) {
      setSlots([]);
      return;
    }

    const [openH, openM] = todayShift.openTime.split(":").map(Number);
    const [closeH, closeM] = todayShift.closeTime.split(":").map(Number);

    let start = setHours(setMinutes(startOfDay(selectedDate), openM), openH);
    let end = setHours(setMinutes(startOfDay(selectedDate), closeM), closeH);

    const now = new Date();
    const slotList = [];

    while (start < end) {
      if (
        format(selectedDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd") &&
        start < now
      ) {
        start = addMinutes(start, 60);
        continue;
      }

      slotList.push(format(start, "hh:mm a"));
      start = addMinutes(start, 60);
    }

    setSlots(slotList);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-2xs">
      {/* Header */}
      <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-gray-100">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-red-50 text-red-600 flex items-center justify-center">
            <Clock size={11} />
          </div>
          <div>
            <h5 className="text-[10px] font-extrabold text-gray-900 uppercase tracking-wide">Schedule Sample Collection</h5>
            <p className="text-[8.5px] text-gray-500">Pick collection date and time slot</p>
          </div>
        </div>
      </div>

      {/* DATE PICKER */}
      <div className="mb-1.5">
        <label className="block text-[9px] font-bold text-gray-700 mb-0.5">
          Collection Date
        </label>

        <div className="relative">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            minDate={new Date()}
            maxDate={addMinutes(new Date(), 14 * 24 * 60)}
            dateFormat="dd MMMM yyyy"
            className="w-full py-1 px-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-red-500 focus:bg-white rounded-md text-[11px] font-bold text-gray-800 outline-hidden transition-all shadow-2xs cursor-pointer"
            placeholderText="Select Date"
          />
        </div>
      </div>

      {/* SLOTS */}
      <div>
        <label className="block text-[9px] font-bold text-gray-700 mb-0.5">
          Available Time Slots ({slots.length})
        </label>

        <div className="flex flex-wrap gap-1 max-h-[90px] overflow-y-auto pr-0.5 custom-scrollbar">
          {slots.length === 0 ? (
            <div className="w-full py-1.5 text-center bg-red-50/50 border border-red-100 rounded-md">
              <span className="text-red-600 text-[10px] font-bold">
                ⚠️ No slots available for this date.
              </span>
            </div>
          ) : (
            slots.map((slot, index) => {
              const isSelected = selectedSlot?.time === slot;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() =>
                    setSelectedSlot({
                      date: selectedDate,
                      time: slot
                    })
                  }
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-md border transition-all flex items-center justify-center gap-1 ${
                    isSelected
                      ? "bg-gradient-to-r from-red-500 to-red-600 border-red-600 text-white shadow-xs"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-red-50/40 hover:border-red-200"
                  }`}
                >
                  <Clock size={9} className={isSelected ? "text-white" : "text-gray-400"} />
                  <span>{slot}</span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Selected Slot Confirmation Bar */}
      {selectedSlot?.time && (
        <div className="mt-1.5 pt-1.5 border-t border-gray-100 flex items-center justify-between text-[10px]">
          <span className="text-gray-500 font-medium">Selected Slot:</span>
          <span className="font-extrabold text-red-700 bg-red-50 px-1.5 py-0.2 rounded border border-red-200">
            {format(new Date(selectedSlot.date || selectedDate), "dd MMM")} at {selectedSlot.time}
          </span>
        </div>
      )}
    </div>
  );
}